import { useMemo } from 'react';
import { Grid, Box, Paper, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckinsList from '@/components/CheckinsList';
import ContainerTitle from '@/components/general/ContainerTitle';
import CustomLink from '@/components/general/CustomLink';
import PriorityItem from '@/components/PriorityItem';
import UnansweredQuestion from '@/components/UnansweredQuestion';
import useProfile from '@/hooks/useProfileV2';
import { TCheckinHistory, AnswerTypes, Answer } from '@/types/api';
import { Paths } from '@/types/routes';
import { UnansweredQuestionType } from './UserCurrentCheckin.container';
import styles from './UserCurrentCheckin.styles';

type UserCurrentCheckinType = {
  checkinsHistory: TCheckinHistory[];
  questions: UnansweredQuestionType[];
  hasSubmitted: boolean;
  addOneMoreLine: (index: string) => void;
  removeOneLine: (index: number, id: string) => void;
  onSubmitHandler: () => void;
  handleUpdatePriorityStatus: (answerId: string, isPriorityFinished: boolean) => void;
  onAnswerHandler: (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    id: string,
    index?: number
  ) => void;
  onCopyPriorityHandler: (value?: any) => void;
  isFormSubmitted?: boolean;
  prioritiesFromLastWeek?: Answer[];
  priorityQuestion?: UnansweredQuestionType;
};

const UserCurrentCheckinComponent = function ({
  questions,
  checkinsHistory,
  onAnswerHandler,
  onCopyPriorityHandler,
  addOneMoreLine,
  hasSubmitted,
  isFormSubmitted,
  removeOneLine,
  onSubmitHandler,
  prioritiesFromLastWeek,
  priorityQuestion,
  handleUpdatePriorityStatus,
}: UserCurrentCheckinType): JSX.Element {
  const { profile } = useProfile();

  const isQuestionsAvailable = !!questions.length;

  const renderQuestions = useMemo(() => {
    if (!isQuestionsAvailable) {
      return (
        <Typography marginY={3} variant="h6" color="primary.light">
          No questions available
        </Typography>
      );
    }

    return questions
      .filter((question) => question.answerType !== AnswerTypes.PRIORITY)
      .map((question) => {
        return (
          <UnansweredQuestion
            disabled={hasSubmitted}
            removeOneLine={removeOneLine}
            addOneMoreLine={addOneMoreLine}
            onAnswerHandler={onAnswerHandler}
            key={question.id}
            isFormSubmitted={isFormSubmitted}
            {...question}
          />
        );
      });
  }, [
    addOneMoreLine,
    hasSubmitted,
    isQuestionsAvailable,
    onAnswerHandler,
    questions,
    removeOneLine,
    isFormSubmitted,
  ]);

  const isButtonDisabled = useMemo(() => {
    if (!isQuestionsAvailable) return true;

    if (!hasSubmitted) return false;

    return true;
  }, [hasSubmitted, isQuestionsAvailable]);

  const renderPrioritiesFromLastWeek = useMemo(() => {
    return (
      !!prioritiesFromLastWeek?.length && (
        <>
          <Typography variant="h6" color="primary" component="span" sx={styles.prioritySubtitle}>
            Mark priorities from your past Check-in as complete
          </Typography>
          {prioritiesFromLastWeek.map((priority: Answer) => (
            <PriorityItem
              description={priority.response}
              isPriorityFinished={!!priority.isFinished}
              id={priority.id}
              key={priority.id}
              onCopyPriorityHandler={onCopyPriorityHandler}
              handleUpdatePriorityStatus={handleUpdatePriorityStatus}
            />
          ))}
        </>
      )
    );
  }, [onCopyPriorityHandler, prioritiesFromLastWeek, handleUpdatePriorityStatus]);

  const renderPriorities = useMemo(() => {
    return (
      priorityQuestion && (
        <UnansweredQuestion
          disabled={hasSubmitted}
          removeOneLine={removeOneLine}
          addOneMoreLine={addOneMoreLine}
          onAnswerHandler={onAnswerHandler}
          isFormSubmitted={isFormSubmitted}
          {...priorityQuestion}
        />
      )
    );
  }, [
    addOneMoreLine,
    hasSubmitted,
    onAnswerHandler,
    priorityQuestion,
    removeOneLine,
    isFormSubmitted,
  ]);

  return (
    <>
      <CustomLink to={Paths.MY_CHECKINS} variant="subtitle1">
        go back
      </CustomLink>
      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <Paper
            variant="outlined"
            sx={styles.userContainer}
            data-testid="user-container"
            key="user-container"
          >
            <Box>
              <Typography
                variant="h4"
                fontWeight={600}
                color="primary"
                sx={styles.userTextsAlign}
                data-testid="user-name"
              >
                {profile.name}
              </Typography>
              <Typography variant="subtitle1" sx={styles.userTextsAlign} data-testid="user-email">
                {profile.email}
              </Typography>
            </Box>
            <Box />
          </Paper>

          <Paper
            variant="outlined"
            sx={styles.containerBox}
            data-testid="priorities-container"
            key="priorities-container"
          >
            <ContainerTitle title="Priorities" />
            {renderPrioritiesFromLastWeek}
            {renderPriorities}
          </Paper>

          <Paper variant="outlined" sx={styles.PrioritiesContainer}>
            <Box>
              <ContainerTitle title="Questions" />
              {renderQuestions}

              <Button
                sx={styles.cancelBtn}
                onClick={() => onSubmitHandler()}
                disabled={isButtonDisabled}
                variant="outlined"
              >
                Submit
              </Button>
              <Button color="auxRed" component={RouterLink} to={Paths.MY_CHECKINS}>
                {hasSubmitted ? 'Go back' : 'Cancel'}
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3} sx={{ marginTop: 2 }}>
          <Paper variant="outlined">
            <Box sx={styles.stickySidebar}>
              <CheckinsList checkinsHistory={checkinsHistory} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default UserCurrentCheckinComponent;
